import { useState, useEffect, useMemo } from "react";
import { searchMovies, submitMovieSelection } from "../service/api";
import { retryApiCall } from "../utils/retry";
import { addToSyncQueue } from "../utils/syncQueue";
import PageWrapper from "./PageWrapper";
import AlertModal from "./AlertModal";

const MIN_FAVORITE_MOVIES = 3;
const MAX_FAVORITE_MOVIES = 5;
const MAX_DISLIKED_MOVIES = 5;
const FAVORITE_SELECTION_ERROR = "Please select at least 3 favorite movies from the suggestions.";

function isSelectedSuggestionMovie(x) {
  return typeof x === "object" && x && x.title && x.tmdb_id != null && String(x.tmdb_id).trim() !== "";
}

function moviesMatch(first, second) {
  if (typeof first === "string") return false;

  const firstTmdbId = first?.tmdb_id ?? null;
  const secondTmdbId = second?.tmdb_id ?? null;
  if (firstTmdbId != null && secondTmdbId != null && firstTmdbId === secondTmdbId) {
    return true;
  }

  const firstTitle = (first?.title ?? "").trim().toLowerCase();
  const secondTitle = (second?.title ?? "").trim().toLowerCase();
  const firstYear = first?.releaseYear ?? null;
  const secondYear = second?.releaseYear ?? null;

  if (firstTitle && secondTitle && firstYear != null && secondYear != null) {
    return firstTitle === secondTitle && String(firstYear) === String(secondYear);
  }
  return firstTitle && secondTitle ? firstTitle === secondTitle : false;
}

function appendMovieWithinLimit(currentMovies, movie, maximum) {
  if (
    currentMovies.length >= maximum ||
    currentMovies.some((selectedMovie) => moviesMatch(selectedMovie, movie))
  ) {
    return currentMovies;
  }
  return [...currentMovies, movie];
}

const MovieSelection = ({ userId, onNext, onBack }) => {
  const [favQuery, setFavQuery] = useState("");
  const [favSuggestions, setFavSuggestions] = useState([]);
  const [favMovies, setFavMovies] = useState(() =>
    JSON.parse(localStorage.getItem("favMovies") || "[]").slice(0, MAX_FAVORITE_MOVIES)
  );
  // const [favReasons, setFavReasons] = useState(() => JSON.parse(localStorage.getItem("favReasons") || "{}"));

  const [dislikeQuery, setDislikeQuery] = useState("");
  const [dislikeSuggestions, setDislikeSuggestions] = useState([]);
  const [dislikedMovies, setDislikedMovies] = useState(() => JSON.parse(localStorage.getItem("dislikedMovies") || "[]"));
  // const [dislikeReasons, setDislikeReasons] = useState(() => JSON.parse(localStorage.getItem("dislikeReasons") || "{}"));

  // const [unlistedFav, setUnlistedFav] = useState(() => localStorage.getItem("unlistedFav") || "");
  // const [unlistedDislike, setUnlistedDislike] = useState(() => localStorage.getItem("unlistedDislike") || "");

  // Alert modal state
  const [alertModal, setAlertModal] = useState({
    isOpen: false,
    type: 'info',
    title: '',
    message: ''
  });

  // ✅ Elapsed time tracking
  const startTime = useMemo(() => {
    const saved = localStorage.getItem("movieSelectionStart");
    const now = Date.now();
    if (!saved) {
      localStorage.setItem("movieSelectionStart", now);
      return now;
    }
    return parseInt(saved, 10);
  }, []);

  // ⬇ Persist everything
  useEffect(() => localStorage.setItem("favMovies", JSON.stringify(favMovies)), [favMovies]);
  // useEffect(() => localStorage.setItem("favReasons", JSON.stringify(favReasons)), [favReasons]);
  useEffect(() => localStorage.setItem("dislikedMovies", JSON.stringify(dislikedMovies)), [dislikedMovies]);
  // useEffect(() => localStorage.setItem("dislikeReasons", JSON.stringify(dislikeReasons)), [dislikeReasons]);
  // useEffect(() => localStorage.setItem("unlistedFav", unlistedFav), [unlistedFav]);
  // useEffect(() => localStorage.setItem("unlistedDislike", unlistedDislike), [unlistedDislike]);

  // ✅ Search TMDB API for movies
  const handleSearchMovies = async (query, isFavorite = true) => {
    if (!query.trim()) {
      isFavorite ? setFavSuggestions([]) : setDislikeSuggestions([]);
      return;
    }

    try {
      const res = await searchMovies(query);

      if (res?.success && Array.isArray(res.movies)) {
        // Log the real thing (not an empty array)
        console.log("TMDB suggestion sample:", res.movies[0]);

        const uniqueMovies = [];
        const seenKeys = new Set();

        res.movies.forEach((movie) => {
          // ✅ Normalize to a stable internal shape
          // IMPORTANT: keep tmdb_id, because that's what we can actually store now
          const normalized = {
            tmdb_id: movie.tmdb_id ?? movie.id ?? null,
            title: movie.title ?? movie.Title ?? "",
            releaseYear: movie.releaseYear ?? movie.year ?? movie.Year ?? null,
            poster_path: movie.poster_path ?? null,
          };

          // Skip garbage rows
          if (!normalized.title) return;

          // ✅ Use tmdb_id when available; otherwise fallback to title+year (weak but ok)
          const key = normalized.tmdb_id != null
            ? `TMDB_${normalized.tmdb_id}`
            : `TITLE_${normalized.title}__${normalized.releaseYear ?? "NOYEAR"}`;

          if (!seenKeys.has(key)) {
            seenKeys.add(key);
            uniqueMovies.push(normalized);
          }
        });

        isFavorite ? setFavSuggestions(uniqueMovies) : setDislikeSuggestions(uniqueMovies);
      } else {
        isFavorite ? setFavSuggestions([]) : setDislikeSuggestions([]);
      }
    } catch (err) {
      console.error("Error fetching movie suggestions:", err);
    }
  };


  // ✅ Add movie from TMDB dropdown
  const addMovie = (movie, isFavorite = true) => {
    if (isFavorite) {
      setFavMovies((currentMovies) =>
        appendMovieWithinLimit(currentMovies, movie, MAX_FAVORITE_MOVIES)
      );
      setFavQuery("");
      setFavSuggestions([]);
    } else {
      setDislikedMovies((currentMovies) =>
        appendMovieWithinLimit(currentMovies, movie, MAX_DISLIKED_MOVIES)
      );
      setDislikeQuery("");
      setDislikeSuggestions([]);
    }
  };



  // ✅ Remove selected movie
  const removeMovie = (movie, isFavorite = true) => {
    if (isFavorite) {
      setFavMovies((currentMovies) => currentMovies.filter((selectedMovie) => selectedMovie !== movie));
      // const updatedReasons = { ...favReasons };
      // delete updatedReasons[movie];
      // setFavReasons(updatedReasons);
    } else {
      setDislikedMovies((currentMovies) => currentMovies.filter((selectedMovie) => selectedMovie !== movie));
      // const updatedReasons = { ...dislikeReasons };
      // delete updatedReasons[movie];
      // setDislikeReasons(updatedReasons);
    }
  };


  const handleSubmit = async () => {
    // const MIN_REASON_LENGTH = 20;

    // const favReasonCount = Object.values(favReasons)
    //   .filter(reason => reason.trim().length >= MIN_REASON_LENGTH).length;

    const favoriteSelectedObjs = favMovies.filter(isSelectedSuggestionMovie);

    if (favoriteSelectedObjs.length < MIN_FAVORITE_MOVIES) {
      setAlertModal({
        isOpen: true,
        type: 'warning',
        title: 'More Movies Needed',
        message: FAVORITE_SELECTION_ERROR
      });
      return;
    }

    // Disliked movies are completely optional - no validation required

    // ✅ Extract manual vs selected (selected = objects from dropdown)
    const isManual = (x) => typeof x === "string";
    const isSelected = (x) => typeof x === "object" && x && x.title;

    const favoriteManual = favMovies.filter(isManual);
    const favoriteSelected = favoriteSelectedObjs.map(m => m.title);
    const favoriteSelectedTmdbIds = favoriteSelectedObjs.map(m => m.tmdb_id);

    const dislikedManual = dislikedMovies.filter(isManual);
    const dislikedSelectedObjs = dislikedMovies.filter(isSelected);
    const dislikedSelected = dislikedSelectedObjs.map(m => m.title);
    const dislikedSelectedTmdbIds = dislikedSelectedObjs.map(m => m.tmdb_id ?? null);

    const endTime = Date.now();
    const elapsedSeconds = Math.floor((endTime - startTime) / 1000);

    const dataToSend = {
      userId,
      favoriteSelected,
      favoriteSelectedTmdbIds,
      favoriteManual,
      dislikedSelected,
      dislikedSelectedTmdbIds,
      dislikedManual,
      movieSelectionStart: new Date(startTime).toISOString(),
      movieSelectionDuration: elapsedSeconds,
    };

    // ✅ DEBUG LOGS:
    console.log("Submitting movie selection payload:", dataToSend);
    console.log("fav selected objs:", favoriteSelectedObjs);
    console.log("fav tmdb ids:", favoriteSelectedTmdbIds);
    console.log("dis selected objs:", dislikedSelectedObjs);
    console.log("dis tmdb ids:", dislikedSelectedTmdbIds);
    console.log("favMovies raw:", favMovies);
    console.log("dislikedMovies raw:", dislikedMovies);
    console.log("favoriteSelectedTmdbIds:", dataToSend.favoriteSelectedTmdbIds);
    console.log("dislikedSelectedTmdbIds:", dataToSend.dislikedSelectedTmdbIds);

    try {
      await retryApiCall(() => submitMovieSelection(dataToSend));
      localStorage.setItem("currentStep", "recommendation");
      onNext();
    } catch (err) {
      console.error("❌ Failed to save movie selection after retries:", err);
      
      const response = err?.response?.data;
      if (err?.response?.status === 400 && response?.error === "at_least_3_favorites_required") {
        setAlertModal({
          isOpen: true,
          type: 'warning',
          title: 'More Movies Needed',
          message: FAVORITE_SELECTION_ERROR
        });
        return;
      }

      // Add to sync queue and let user continue
      addToSyncQueue(
        'Movie Selection',
        'submitMovieSelection',
        dataToSend
      );
      
      console.log("📦 Movie selection queued for background sync");
      
      // Let user continue - sync will happen in background
      localStorage.setItem("currentStep", "recommendation");
      onNext();
    }
  };


  return (
    <PageWrapper>
      <h2 style={{ textAlign: "center" }}>🎬 Favorite & Disliked Movies</h2>

      {/* Favorite Movies Input */}
      <label><b>List some movies you really enjoyed:</b></label>
      <p style={{ fontSize: "var(--font-size-xs)", fontStyle: "italic" }}>
        Please select at least 3 favourite movies from the suggestions. You may select up to 5.
      </p>
      <p style={{ fontSize: "var(--font-size-xs)", fontStyle: "italic" }}>
        You can type the title in your own language. We will automatically match it to the correct film.
      </p>
      <input
        type="text"
        placeholder="Type a movie title..."
        value={favQuery}
        data-testid="movie-selection-favorite-input"
        onChange={(e) => {
          setFavQuery(e.target.value);
          handleSearchMovies(e.target.value, true);
        }}
        style={{ width: "100%", padding: "5px" }}
      />

      {/* Favorite Suggestions Dropdown */}
      <ul style={{ listStyle: "none", padding: 0, maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc", background: "white" }}>
        {favSuggestions.slice(0, 10).map((movie, index) => (
          <li
            key={index}
            style={{ padding: "5px", cursor: "pointer" }}
            data-testid={`movie-selection-favorite-suggestion-${movie.tmdb_id ?? index}`}
            onClick={() => addMovie(movie, true)}
          >
            {movie.title} ({movie.releaseYear})
          </li>
        ))}
      </ul>

      {/* Selected Favorites */}
      <ul>
        {favMovies.map((movie, index) => (
          <li
            key={index}
            style={{ margin: "5px 0" }}
            data-testid="movie-selection-favorite-selected"
          >
            {movie.title}
            <button
              onClick={() => removeMovie(movie, true)}
              data-testid={`movie-selection-favorite-remove-${movie.tmdb_id ?? index}`}
            >
              ❌
            </button>
          </li>
        ))}
      </ul>

      {/* <hr /> */}

      {/* Favorite Movie Reasons */}
      {/* {favMovies.length > 0 && (
        <>
          <label><b>What did you enjoy about each movie?</b></label>
          <p style={{ fontSize: "small", fontStyle: "italic", marginBottom: "10px" }}>
            Please write a couple of sentences for at least 3 of the titles you selected.
          </p>
          {favMovies.map((movie, index) => {
            const title = typeof movie === "string" ? movie : movie.title;
            return (
              <div key={index} style={{ marginBottom: "15px" }}>
                <p style={{ marginBottom: "5px", fontWeight: "bold" }}>{title}</p>
                <textarea
                  placeholder={`Why did you enjoy "${title}"?`}
                  value={favReasons[title] || ""}
                  onChange={(e) => setFavReasons({ ...favReasons, [title]: e.target.value })}
                  style={{ width: "100%", height: "60px", padding: "5px" }}
                />
              </div>
            );
          })}
        </>
      )} */}

      {/* Always Show Unlisted Favorite Description
      <label><b>Liked a movie but can't recall the title?</b></label>
      <p style={{ fontSize: "small", fontStyle: "italic", marginBottom: "8px" }}>
        You can describe it here - anything helps us understand your taste.
      </p>
      <textarea
        placeholder="Describe the movie or type of story you truly enjoy(ed)..."
        value={unlistedFav}
        onChange={(e) => setUnlistedFav(e.target.value)}
        style={{ width: "100%", height: "60px", padding: "5px", marginBottom: "20px" }}
      /> */}

      <hr />

      {/* Disliked Movies Input */}
      <label><b>You may also list movies you dislike:</b></label>
      <p style={{ fontSize: "var(--font-size-xs)", fontStyle: "italic" }}>
        If possible, select at least 1 movie. This step is optional, and you may select up to 5.
      </p>
      <input
        type="text"
        placeholder="Type a movie title..."
        value={dislikeQuery}
        data-testid="movie-selection-dislike-input"
        onChange={(e) => {
          setDislikeQuery(e.target.value);
          handleSearchMovies(e.target.value, false);
        }}
        style={{ width: "100%", padding: "5px" }}
      />

      {/* Dislike Suggestions Dropdown */}
      <ul style={{ listStyle: "none", padding: 0, maxHeight: "150px", overflowY: "auto", border: "1px solid #ccc", background: "white" }}>
        {dislikeSuggestions.slice(0, 10).map((movie, index) => (
          <li
            key={index}
            style={{ padding: "5px", cursor: "pointer" }}
            data-testid={`movie-selection-dislike-suggestion-${movie.tmdb_id ?? index}`}
            onClick={() => addMovie(movie, false)}
          >
            {movie.title} ({movie.releaseYear})
          </li>
        ))}
      </ul>

      {/* Selected Dislikes */}
      <ul>
        {dislikedMovies.map((movie, index) => (
          <li
            key={index}
            style={{ margin: "5px 0" }}
            data-testid="movie-selection-dislike-selected"
          >
            {movie.title}
            <button
              onClick={() => removeMovie(movie, false)}
              data-testid={`movie-selection-dislike-remove-${movie.tmdb_id ?? index}`}
            >
              ❌
            </button>
          </li>
        ))}
      </ul>

      {/* <hr /> */}

      {/* Dislike Movie Reasons */}
      {/* {dislikedMovies.length > 0 && (
        <>
          <label><b>What did you dislike about each movie?</b></label>
          <p style={{ fontSize: "small", fontStyle: "italic", marginBottom: "10px" }}>
            Please write a couple of sentences for at least 1 of the titles you selected.
          </p>
          {dislikedMovies.map((movie, index) => {
            const title = typeof movie === "string" ? movie : movie.title;
            return (
              <div key={index} style={{ marginBottom: "15px" }}>
                <p style={{ marginBottom: "5px", fontWeight: "bold" }}>{title}</p>
                <textarea
                  placeholder={`Why did you dislike "${title}"?`}
                  value={dislikeReasons[title] || ""}
                  onChange={(e) => setDislikeReasons({ ...dislikeReasons, [title]: e.target.value })}
                  style={{ width: "100%", height: "60px", padding: "5px" }}
                />
              </div>
            );
          })}
        </>
      )} */}

      {/* Always Show Unlisted Dislike Description
      <label><b>Disliked a movie but can't remember the title?</b></label>
      <p style={{ fontSize: "small", fontStyle: "italic", marginBottom: "8px" }}>
        Describe it below to avoid poor recommendations.
      </p>
      <textarea
        placeholder="Describe a movie or type of story you dislike(d)..."
        value={unlistedDislike}
        onChange={(e) => setUnlistedDislike(e.target.value)}
        style={{ width: "100%", height: "60px", padding: "5px", marginBottom: "20px" }}
      /> */}

      {/* Navigation Buttons */}
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: "20px" }}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            style={{
              padding: "10px",
              fontSize: "var(--font-size-base)",
              backgroundColor: "var(--color-disabled)",
              color: "white",
              border: "none",
              cursor: "pointer"
            }}
          >
            ⬅ Back
          </button>
        )}
        <button
          onClick={handleSubmit}
          data-testid="movie-selection-submit"
          style={{
            padding: "10px",
            fontSize: "var(--font-size-base)",
            backgroundColor: "var(--color-success)",
            color: "white",
            border: "none",
            cursor: "pointer"
          }}
        >
          Submit & Proceed ➡
        </button>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </PageWrapper>
  );
};

export default MovieSelection;
